import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  alpha,
  useTheme
} from '@mui/material';
import MilitaryTechIcon from '@mui/icons-material/MilitaryTech';
import MainLayout from '../../components/layout/MainLayout';
import TierBadge from '../../components/ranked/TierBadge';
import RatingDelta from '../../components/ranked/RatingDelta';
import StreakFlame from '../../components/ranked/StreakFlame';
import { getStudents } from '../../services/teacherService';
import { getRankedRoster } from '../../services/rankedService';
import { Student } from '../../types/teacher';
import { RankedRosterEntry } from '../../types/ranked';

/** Ladder gold — fixed, for the same reason tier colours are. */
const GOLD_ACCENT = '#F39C12';

type SortKey = 'name' | 'rating' | 'rank' | 'games' | 'xp' | 'streak';

interface Row {
  student: Student;
  entry: RankedRosterEntry | null;
}

/**
 * A teacher's whole class ranked in one table.
 *
 * Unranked students sort last but are shown explicitly rather than filtered
 * out: "who has not played a rated quiz yet" is exactly the question a teacher
 * needs this page to answer, and hiding those rows would answer the opposite.
 */
const RankedRosterPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [rows, setRows] = useState<Row[]>([]);
  const [totalRanked, setTotalRanked] = useState<number>(0);
  const [seasonName, setSeasonName] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('rating');
  const [descending, setDescending] = useState<boolean>(true);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);

        const students = await getStudents();
        const roster = await getRankedRoster(students.map(student => student.id));
        const byUserId = new Map(roster.entries.map(entry => [entry.userId, entry]));

        setRows(students.map(student => ({ student, entry: byUserId.get(student.id) ?? null })));
        setTotalRanked(roster.totalRanked);
        setSeasonName(roster.season.name);
      } catch (err) {
        console.error('Error loading class ranking:', err);
        setError(err instanceof Error ? err.message : 'Failed to load class ranking');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const sorted = useMemo(() => {
    const ordered = [...rows].sort((a, b) => {
      // An unrated student always sorts last, whichever column is active and
      // in whichever direction — they have no value to compare, and floating
      // them to the top on an ascending sort would read as "worst in class".
      const aRanked = a.entry?.rank != null;
      const bRanked = b.entry?.rank != null;
      if (aRanked !== bRanked) {
        return aRanked ? -1 : 1;
      }

      const compared = compare(sortKey, a, b);
      return descending ? -compared : compared;
    });

    return ordered;
  }, [rows, sortKey, descending]);

  const toggleSort = (key: SortKey) => {
    if (key === sortKey) {
      setDescending(previous => !previous);
      return;
    }
    setSortKey(key);
    // Names read naturally A–Z; every other column is "best first".
    setDescending(key !== 'name');
  };

  if (loading) {
    return (
      <MainLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <Paper
        elevation={0}
        sx={{
          mb: 4,
          p: 4,
          borderRadius: 4,
          background: `linear-gradient(135deg, ${alpha(GOLD_ACCENT, 0.1)} 0%, ${alpha(
            theme.palette.primary.main,
            0.1
          )} 100%)`,
          border: `1px solid ${alpha(GOLD_ACCENT, 0.2)}`
        }}
      >
        <Typography
          variant="h3"
          fontWeight={800}
          sx={{
            background: `linear-gradient(135deg, ${GOLD_ACCENT}, ${theme.palette.primary.main})`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5
          }}
        >
          <MilitaryTechIcon sx={{ fontSize: 40, color: GOLD_ACCENT }} />
          Class Ranking
        </Typography>
        <Typography variant="h6" color="text.secondary" fontWeight={400}>
          {seasonName
            ? `${seasonName} · ranks are against every mathlete in the league, not just your class.`
            : 'Ranks are against every mathlete in the league, not just your class.'}
        </Typography>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}` }}>
        <CardContent sx={{ p: 0 }}>
          {rows.length === 0 ? (
            <Typography color="text.secondary" sx={{ p: 3 }}>
              You have no students yet.
            </Typography>
          ) : (
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <SortableCell
                      label="Student"
                      column="name"
                      sortKey={sortKey}
                      descending={descending}
                      onSort={toggleSort}
                    />
                    <TableCell>Tier</TableCell>
                    <SortableCell
                      label="Rating"
                      column="rating"
                      align="right"
                      sortKey={sortKey}
                      descending={descending}
                      onSort={toggleSort}
                    />
                    <SortableCell
                      label="Season rank"
                      column="rank"
                      align="right"
                      sortKey={sortKey}
                      descending={descending}
                      onSort={toggleSort}
                    />
                    <SortableCell
                      label="Quizzes"
                      column="games"
                      align="right"
                      sortKey={sortKey}
                      descending={descending}
                      onSort={toggleSort}
                    />
                    <TableCell align="right">Last change</TableCell>
                    <SortableCell
                      label="Season XP"
                      column="xp"
                      align="right"
                      sortKey={sortKey}
                      descending={descending}
                      onSort={toggleSort}
                    />
                    <SortableCell
                      label="Streak"
                      column="streak"
                      align="right"
                      sortKey={sortKey}
                      descending={descending}
                      onSort={toggleSort}
                    />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sorted.map(({ student, entry }) => (
                    <TableRow
                      key={student.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/teacher/students/${student.id}`)}
                      data-testid={`ranked-row-${student.id}`}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {student.firstName} {student.lastName}
                        </Typography>
                      </TableCell>

                      {entry && entry.rank != null ? (
                        <>
                          <TableCell>
                            <TierBadge
                              tier={entry.tier.name}
                              label={entry.tier.label}
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              variant="body2"
                              sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}
                            >
                              {entry.rating}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              variant="body2"
                              sx={{ fontVariantNumeric: 'tabular-nums' }}
                            >
                              {ordinal(entry.rank)} of {totalRanked}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              variant="body2"
                              sx={{ fontVariantNumeric: 'tabular-nums' }}
                            >
                              {entry.gamesPlayed}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            {entry.lastEvent ? (
                              <RatingDelta delta={entry.lastEvent.delta} />
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                —
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              variant="body2"
                              sx={{ fontVariantNumeric: 'tabular-nums' }}
                            >
                              {entry.seasonXp.toLocaleString()}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <StreakFlame streak={entry.streak} />
                          </TableCell>
                        </>
                      ) : (
                        <TableCell colSpan={7}>
                          <Typography variant="body2" color="text.secondary">
                            Not ranked yet — needs a live quiz with 3 or more mathletes
                          </Typography>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </MainLayout>
  );
};

const SortableCell: React.FC<{
  label: string;
  column: SortKey;
  align?: 'left' | 'right';
  sortKey: SortKey;
  descending: boolean;
  onSort: (key: SortKey) => void;
}> = ({ label, column, align = 'left', sortKey, descending, onSort }) => (
  <TableCell align={align} sortDirection={sortKey === column ? (descending ? 'desc' : 'asc') : false}>
    <TableSortLabel
      active={sortKey === column}
      direction={descending ? 'desc' : 'asc'}
      onClick={() => onSort(column)}
    >
      {label}
    </TableSortLabel>
  </TableCell>
);

function compare(key: SortKey, a: Row, b: Row): number {
  switch (key) {
    case 'name':
      return `${a.student.lastName} ${a.student.firstName}`.localeCompare(
        `${b.student.lastName} ${b.student.firstName}`
      );
    case 'rating':
      return (a.entry?.rating ?? 0) - (b.entry?.rating ?? 0);
    case 'rank':
      // A better rank is a smaller number, so invert it to keep "descending"
      // meaning "best first" the way it does for every other column.
      return (b.entry?.rank ?? Number.MAX_SAFE_INTEGER) - (a.entry?.rank ?? Number.MAX_SAFE_INTEGER);
    case 'games':
      return (a.entry?.gamesPlayed ?? 0) - (b.entry?.gamesPlayed ?? 0);
    case 'xp':
      return (a.entry?.seasonXp ?? 0) - (b.entry?.seasonXp ?? 0);
    case 'streak':
      return (a.entry?.streak.current ?? 0) - (b.entry?.streak.current ?? 0);
    default:
      return 0;
  }
}

function ordinal(n: number): string {
  const suffix = n === 1 ? 'st' : n === 2 ? 'nd' : n === 3 ? 'rd' : 'th';
  return `${n}${suffix}`;
}

export default RankedRosterPage;
